// Addresses typed or pasted, checked with the service as the list, the scope or the sections change. The
// warnings never block a save, and a failed check only leaves them out: the save answers them again.
import React, { useEffect, useState } from "react";
import { IconButton, TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useCheckRecipientsMutation } from "services/scheduledReports";
import { addAddresses, addressWarnings } from "utils/reportDefinition";
import { fieldError } from "utils/reportErrors";

const CHECK_DELAY_MS = 600;

export default function RecipientList({ recipients, onChange, scope, sections, reportId, errors }) {
  const [text, setText] = useState("");
  const [invalid, setInvalid] = useState([]);
  const [results, setResults] = useState({});
  const [check] = useCheckRecipientsMutation();

  const sectionKeys = sections.map((s) => s.key);
  const checkKey = JSON.stringify({ scope, sectionKeys, recipients });
  useEffect(() => {
    const ready = recipients.length && sectionKeys.length && (scope.mode === "all" || scope.serviceIds.length);
    if (!ready) return undefined;
    const timer = setTimeout(async () => {
      const res = await check({
        token: window.localStorage.getItem("token"),
        body: {
          scope,
          sections: sectionKeys.map((key) => ({ key })),
          addresses: recipients,
          ...(reportId ? { reportId } : {}),
        },
      });
      const list = res.data && res.data.payload && res.data.payload.results;
      if (list) setResults(Object.fromEntries(list.map((r) => [r.address, r])));
    }, CHECK_DELAY_MS);
    return () => clearTimeout(timer);
  }, [checkKey]);

  const commit = () => {
    if (!text.trim()) return;
    const added = addAddresses(recipients, text);
    onChange(added.recipients);
    setInvalid(added.invalid);
    setText(added.invalid.join(", "));
  };

  return (
    <div>
      <TextField
        size="small"
        fullWidth
        multiline
        maxRows={4}
        label="Add addresses"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setInvalid([]);
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            commit();
          }
        }}
        error={invalid.length > 0 || !!(errors && errors.recipients)}
        helperText={
          invalid.length
            ? `Not a valid address: ${invalid.join(", ")}`
            : (errors && errors.recipients) || "Type or paste addresses, separated by commas or one per line, then press Enter."
        }
      />
      {recipients.length ? null : (
        <p style={{ fontSize: "13px", color: "#b26a00", margin: "8px 0 0 0" }}>With no address on the list, this report is not sent.</p>
      )}
      <div style={{ marginTop: "8px" }}>
        {recipients.map((address, i) => (
          <div key={address} style={{ padding: "2px 0", borderBottom: "1px solid #f0f0f0" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "14px" }}>
              <span style={{ wordBreak: "break-all" }}>{address}</span>
              <IconButton
                size="small"
                aria-label={`Remove ${address}`}
                onClick={() => onChange(recipients.filter((a) => a !== address))}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
            {addressWarnings(results[address]).map((line) => (
              <p key={line} style={{ fontSize: "12px", color: "#b26a00", margin: "0 0 4px 0" }}>
                {line}
              </p>
            ))}
            {fieldError(errors, `recipients.${i}`) ? (
              <p style={{ fontSize: "12px", color: "#bf3535", margin: "0 0 4px 0" }}>{fieldError(errors, `recipients.${i}`)}</p>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
